import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Alert,
  Stack,
  Paper,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Close,
  Business,
  FactoryOutlined,
  Search as SearchIcon,
  ImportExport,
} from "@mui/icons-material";
import {
  getProveedoresElegiblesTraspasoFabrica,
  traspasoProveedorAFabrica,
} from "../../api/ProveedoresyFabricas/proveedoresApi";
import { getFabricas, getFabricasTipo } from "../../api/ProveedoresyFabricas/fabricasApi";

const ACCENT_PROV = "#90b2b7";
const ACCENT_FAB = "#ffac7e";
const ACCENT_PROV_HOVER = "#7a9ea3";

const listPaperSx = (theme) => ({
  maxHeight: 280,
  overflow: "auto",
  borderRadius: 2,
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  bgcolor: alpha(theme.palette.background.paper, 0.9),
  "& .MuiListItemButton-root": {
    borderRadius: 1,
    mx: 0.5,
    my: 0.25,
  },
  "& .MuiListItemButton-root.Mui-selected": {
    bgcolor: alpha(ACCENT_PROV, 0.14),
    borderLeft: `3px solid ${ACCENT_PROV}`,
    "&:hover": { bgcolor: alpha(ACCENT_PROV, 0.2) },
  },
});

const ProveedoresTraspasoFabricaDialog = ({ open, onClose, onComplete }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [step, setStep] = useState("pick");
  const [eligible, setEligible] = useState([]);
  const [fabricas, setFabricas] = useState([]);
  const [tiposFabrica, setTiposFabrica] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [searchProv, setSearchProv] = useState("");
  const [searchFab, setSearchFab] = useState("");
  const [selected, setSelected] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [idFabricaTipo, setIdFabricaTipo] = useState("");
  const [keepTrader, setKeepTrader] = useState("yes");

  const resetState = useCallback(() => {
    setStep("pick");
    setEligible([]);
    setFabricas([]);
    setTiposFabrica([]);
    setLoading(false);
    setSubmitting(false);
    setError("");
    setSearchProv("");
    setSearchFab("");
    setSelected(null);
    setDragOver(false);
    setIdFabricaTipo("");
    setKeepTrader("yes");
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [e, f, tipos] = await Promise.all([
        getProveedoresElegiblesTraspasoFabrica(),
        getFabricas(),
        getFabricasTipo(),
      ]);
      setEligible(Array.isArray(e) ? e : []);
      setFabricas(Array.isArray(f) ? f : []);
      setTiposFabrica(Array.isArray(tipos) ? tipos : []);
    } catch (err) {
      console.error(err);
      setError(t("ProveedoresPage.transferError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    loadData();
  }, [open, loadData, resetState]);

  const filteredEligibleSorted = useMemo(() => {
    const q = searchProv.trim().toLowerCase();
    const list = eligible.filter((p) => {
      if (!q) return true;
      const tipo = p.tipoProveedor?.ProveedorTipo || "";
      return `${p.N_PROVEEDOR || ""} ${tipo} ${p.PAIS || ""}`.toLowerCase().includes(q);
    });
    return [...list].sort((a, b) =>
      (a.N_PROVEEDOR || "").localeCompare(b.N_PROVEEDOR || "", undefined, {
        sensitivity: "base",
        numeric: true,
      })
    );
  }, [eligible, searchProv]);

  const filteredFabricasSorted = useMemo(() => {
    const q = searchFab.trim().toLowerCase();
    const list = fabricas.filter((fab) => {
      if (!q) return true;
      const tipo = fab.tipoFabrica?.NombreTipo || "";
      return `${fab.NombreFabrica || ""} ${tipo} ${fab.Pais || ""}`.toLowerCase().includes(q);
    });
    return [...list].sort((a, b) =>
      (a.NombreFabrica || "").localeCompare(b.NombreFabrica || "", undefined, {
        sensitivity: "base",
        numeric: true,
      })
    );
  }, [fabricas, searchFab]);

  const handleDragStart = (e, proveedor) => {
    e.dataTransfer.setData("text/plain", String(proveedor.CODPROV));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOverZone = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDropZone = (e) => {
    e.preventDefault();
    setDragOver(false);
    const raw = e.dataTransfer.getData("text/plain");
    const cod = Number(raw);
    if (Number.isNaN(cod)) return;
    const prov = eligible.find((x) => x.CODPROV === cod);
    if (!prov) return;
    setSelected(prov);
    setStep("confirm");
  };

  const goConfirm = () => {
    if (!selected) return;
    setStep("confirm");
  };

  const handleExecute = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError("");
    try {
      const tipoVal =
        idFabricaTipo === "" || idFabricaTipo === undefined
          ? null
          : Number(idFabricaTipo);
      const payload = {
        keepAsTrader: keepTrader === "yes",
        id_FabricaTipo: Number.isNaN(tipoVal) ? null : tipoVal,
      };
      const result = await traspasoProveedorAFabrica(selected.CODPROV, payload);
      if (onComplete) await onComplete(result);
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.details ||
        t("ProveedoresPage.transferError");
      setError(typeof msg === "string" ? msg : t("ProveedoresPage.transferError"));
    } finally {
      setSubmitting(false);
    }
  };

  const dialogPaperSx = {
    borderRadius: 3,
    overflow: "hidden",
    boxShadow: `0 24px 48px -12px ${alpha(theme.palette.common.black, 0.18)}`,
  };

  const dropSx = {
    minHeight: 112,
    border: 2,
    borderStyle: "dashed",
    borderColor: dragOver ? ACCENT_FAB : alpha(theme.palette.divider, 0.9),
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    px: 2,
    py: 2.5,
    mb: 2,
    backgroundColor: dragOver
      ? alpha(ACCENT_FAB, 0.12)
      : alpha(theme.palette.action.hover, 0.35),
    transition: theme.transitions.create(["border-color", "background-color"], {
      duration: theme.transitions.duration.shorter,
    }),
  };

  const fieldRoundedSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 2 },
  };

  const btnExecuteSx = {
    px: 2.5,
    py: 1,
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 2,
    bgcolor: ACCENT_FAB,
    color: theme.palette.getContrastText(ACCENT_FAB),
    boxShadow: "none",
    "&:hover": {
      bgcolor: alpha(ACCENT_FAB, 0.88),
      boxShadow: `0 8px 20px ${alpha(ACCENT_FAB, 0.35)}`,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          background: `linear-gradient(120deg, ${alpha(ACCENT_PROV, 0.22)} 0%, ${alpha(
            theme.palette.background.paper,
            1,
          )} 42%, ${alpha(ACCENT_FAB, 0.14)} 100%)`,
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor: alpha(ACCENT_PROV, 0.35),
                color: theme.palette.text.primary,
              }}
            >
              <ImportExport />
            </Box>
            <Box>
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, letterSpacing: 0.9, color: "text.secondary", lineHeight: 1.2 }}
              >
                CRM
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.3, mt: 0.25 }}>
                {t("ProveedoresPage.transferDialogTitle")}
              </Typography>
              {step === "pick" ? (
                <Chip
                  size="small"
                  label={t("ProveedoresPage.transferStepPick")}
                  sx={{ mt: 1, fontWeight: 700, bgcolor: alpha(ACCENT_PROV, 0.2) }}
                />
              ) : (
                <Chip
                  size="small"
                  label={t("ProveedoresPage.transferStepConfirm")}
                  color="warning"
                  variant="outlined"
                  sx={{ mt: 1, fontWeight: 700, borderColor: ACCENT_FAB }}
                />
              )}
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("ProveedoresPage.transferClose")}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
              borderRadius: 2,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Alert severity="info" variant="outlined" sx={{ mb: 2.5, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
            {t("ProveedoresPage.transferDialogIntro")}
          </Typography>
        </Alert>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              py: 5,
              textAlign: "center",
              borderRadius: 2,
              borderStyle: "dashed",
            }}
          >
            <CircularProgress size={36} sx={{ color: ACCENT_PROV, mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {t("ProveedoresPage.transferLoading")}
            </Typography>
          </Paper>
        ) : (
          <>
            {step === "pick" && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems="stretch">
                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    p: 2,
                    borderRadius: 2,
                    borderColor: alpha(ACCENT_PROV, 0.35),
                    bgcolor: alpha(ACCENT_PROV, 0.04),
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Business sx={{ color: ACCENT_PROV, fontSize: 22 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {t("ProveedoresPage.transferLeftTitle")}
                    </Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t("ProveedoresPage.transferSearchSuppliers")}
                    value={searchProv}
                    onChange={(e) => setSearchProv(e.target.value)}
                    sx={{ ...fieldRoundedSx, mb: 1.5 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "text.secondary", fontSize: 22 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {filteredEligibleSorted.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {t("ProveedoresPage.transferNoEligible")}
                    </Typography>
                  ) : (
                    <List dense disablePadding sx={listPaperSx(theme)}>
                      {filteredEligibleSorted.map((p) => (
                        <ListItemButton
                          key={p.CODPROV}
                          draggable
                          selected={selected?.CODPROV === p.CODPROV}
                          onDragStart={(e) => handleDragStart(e, p)}
                          onClick={() => setSelected(p)}
                        >
                          <Business sx={{ color: ACCENT_PROV, mr: 1.25, fontSize: 22, flexShrink: 0 }} />
                          <ListItemText
                            primary={p.N_PROVEEDOR || "—"}
                            secondary={`${p.tipoProveedor?.ProveedorTipo || ""} · ${p.PAIS || ""}`}
                            primaryTypographyProps={{ variant: "body2", fontWeight: 600, noWrap: true }}
                            secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!selected}
                      onClick={goConfirm}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 2,
                        bgcolor: ACCENT_PROV,
                        boxShadow: "none",
                        "&:hover": { bgcolor: ACCENT_PROV_HOVER, boxShadow: "none" },
                      }}
                    >
                      {t("ProveedoresPage.transferContinue")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!selected}
                      onClick={() => setSelected(null)}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                    >
                      {t("ProveedoresPage.transferClearSelection")}
                    </Button>
                  </Stack>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1.15,
                    minWidth: 0,
                    p: 2,
                    borderRadius: 2,
                    borderColor: alpha(ACCENT_FAB, 0.4),
                    bgcolor: alpha(ACCENT_FAB, 0.05),
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <FactoryOutlined sx={{ color: ACCENT_FAB, fontSize: 24 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {t("ProveedoresPage.transferRightTitle")}
                    </Typography>
                  </Stack>

                  <Box
                    onDragEnter={() => setDragOver(true)}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
                    }}
                    onDragOver={handleDragOverZone}
                    onDrop={handleDropZone}
                    sx={dropSx}
                  >
                    <FactoryOutlined sx={{ fontSize: 36, color: ACCENT_FAB, mb: 1, opacity: 0.85 }} />
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 280 }}>
                      {t("ProveedoresPage.transferDropHint")}
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t("ProveedoresPage.transferSearchFactories")}
                    value={searchFab}
                    onChange={(e) => setSearchFab(e.target.value)}
                    sx={{ ...fieldRoundedSx, mb: 1.5 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "text.secondary", fontSize: 22 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <List
                    dense
                    disablePadding
                    sx={{
                      ...listPaperSx(theme),
                      maxHeight: 220,
                      "& .MuiListItemButton-root.Mui-disabled": { opacity: 0.75 },
                    }}
                  >
                    {filteredFabricasSorted.map((fab) => (
                      <ListItemButton key={fab.id_Fabrica} disabled>
                        <FactoryOutlined
                          sx={{ color: ACCENT_FAB, mr: 1.25, fontSize: 22, flexShrink: 0, opacity: 0.9 }}
                        />
                        <ListItemText
                          primary={fab.NombreFabrica}
                          secondary={`${fab.tipoFabrica?.NombreTipo || ""} · ${fab.Pais || ""}`}
                          primaryTypographyProps={{ variant: "body2", fontWeight: 600, noWrap: true }}
                          secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              </Stack>
            )}

            {step === "confirm" && selected && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.divider, 0.15),
                  bgcolor: alpha(theme.palette.background.paper, 1),
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>
                  {t("ProveedoresPage.transferStepConfirm")}:{" "}
                  <Box component="span" sx={{ color: ACCENT_PROV }}>
                    {selected.N_PROVEEDOR}
                  </Box>
                </Typography>

                <FormControl fullWidth size="small" sx={{ ...fieldRoundedSx, mb: 2 }}>
                  <InputLabel id="tf-tipo-fab">{t("ProveedoresPage.transferFactoryType")}</InputLabel>
                  <Select
                    labelId="tf-tipo-fab"
                    label={t("ProveedoresPage.transferFactoryType")}
                    value={idFabricaTipo === "" ? "" : String(idFabricaTipo)}
                    onChange={(e) => setIdFabricaTipo(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>{t("ProveedoresPage.transferFactoryTypeNone")}</em>
                    </MenuItem>
                    {tiposFabrica.map((tipo) => (
                      <MenuItem key={tipo.id_FabricaTipo} value={String(tipo.id_FabricaTipo)}>
                        {tipo.NombreTipo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.action.hover, 0.2),
                    borderColor: alpha(theme.palette.divider, 0.12),
                  }}
                >
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
                      {t("ProveedoresPage.transferKeepTraderTitle")}
                    </FormLabel>
                    <RadioGroup value={keepTrader} onChange={(e) => setKeepTrader(e.target.value)}>
                      <FormControlLabel
                        value="yes"
                        control={<Radio size="small" sx={{ color: ACCENT_PROV }} />}
                        label={<Typography variant="body2">{t("ProveedoresPage.transferKeepTraderYes")}</Typography>}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio size="small" sx={{ color: ACCENT_PROV }} />}
                        label={<Typography variant="body2">{t("ProveedoresPage.transferKeepTraderNo")}</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>
                </Paper>
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          bgcolor: alpha(theme.palette.background.default, 0.65),
        }}
      >
        {step === "confirm" && !loading && (
          <Button
            onClick={() => setStep("pick")}
            disabled={submitting}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("ProveedoresPage.transferBack")}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none", fontWeight: 600 }}>
          {t("ProveedoresPage.transferClose")}
        </Button>
        {step === "confirm" && selected && !loading ? (
          <Button variant="contained" onClick={handleExecute} disabled={submitting} sx={btnExecuteSx}>
            {submitting ? <CircularProgress size={22} color="inherit" /> : t("ProveedoresPage.transferExecute")}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default ProveedoresTraspasoFabricaDialog;
